import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Puzzle,
  RefreshCw,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import {
  PluginHealthSidebar,
  ToolSearchBar,
  ToolCatalog,
  ToolTestingPanel,
} from '@/components/plugins'
import {
  getPluginHealthStatus,
  getToolCatalog,
  executeToolFromUI,
} from '@/api/plugins'
import type {
  PluginHealth,
  ToolCatalogItem,
  ToolFilters,
  ViewMode,
  ToolExecResponse,
} from '@/types/plugins'

const defaultFilters: ToolFilters = {
  search: '',
  plugin: null,
  dangerLevel: null,
  minSatisfaction: null,
  sortBy: 'name',
  sortOrder: 'asc',
}

export default function Plugins() {
  const [plugins, setPlugins] = useState<PluginHealth[]>([])
  const [tools, setTools] = useState<ToolCatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<ToolFilters>(defaultFilters)
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null)

  const [testingTool, setTestingTool] = useState<ToolCatalogItem | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [pluginsData, toolsData] = await Promise.all([
        getPluginHealthStatus(),
        getToolCatalog(),
      ])
      setPlugins(pluginsData)
      setTools(toolsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plugins')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Sync plugin filter with sidebar selection
  useEffect(() => {
    if (selectedPlugin !== filters.plugin) {
      setFilters((prev) => ({ ...prev, plugin: selectedPlugin }))
    }
  }, [selectedPlugin, filters.plugin])

  // Get unique plugin names for filter dropdown
  const pluginOptions = useMemo(
    () => [...new Set(tools.map((t) => t.plugin))].sort(),
    [tools]
  )

  // Filter tools by selected plugin
  const filteredByPlugin = useMemo(() => {
    if (!filters.plugin) return tools
    return tools.filter((t) => t.plugin === filters.plugin)
  }, [tools, filters.plugin])

  // Handle tool execution
  const handleExecuteTool = useCallback(
    async (
      tool: ToolCatalogItem,
      args: Record<string, unknown>
    ): Promise<ToolExecResponse> => {
      return executeToolFromUI(tool.name, { args })
    },
    []
  )

  if (loading && plugins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
          <div className="absolute inset-0 w-16 h-16 bg-accent/20 rounded-2xl animate-ping" />
        </div>
        <p className="text-text-muted">Loading tool catalog...</p>
      </div>
    )
  }

  if (error && plugins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-4">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <p className="text-text-muted text-center">{error}</p>
        <button onClick={fetchData} className="btn-primary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 sm:p-6 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-500/5 flex items-center justify-center">
                <Puzzle className="w-5 h-5 text-violet-400" />
              </div>
              Tool Command Center
            </h1>
            <p className="text-text-muted mt-1 text-sm">
              Explore, test, and manage {tools.length} tools across{' '}
              {plugins.length} plugins
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-3 rounded-xl hover:bg-surface-hover border border-surface-border transition-all hover:scale-105 active:scale-95"
            title="Refresh"
          >
            <RefreshCw
              className={`w-5 h-5 text-text-muted ${loading ? 'animate-spin' : ''}`}
            />
          </button>
        </div>

        {/* Search and Filters */}
        <ToolSearchBar
          filters={filters}
          onFiltersChange={setFilters}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          pluginOptions={pluginOptions}
          resultCount={filteredByPlugin.length}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 pt-4 overflow-hidden">
        <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Left Sidebar - Plugin Health */}
          <div className="lg:col-span-3 h-full overflow-hidden">
            <PluginHealthSidebar
              plugins={plugins}
              loading={loading}
              selectedPlugin={selectedPlugin}
              onSelectPlugin={(plugin) => {
                setSelectedPlugin(plugin)
                setFilters((prev) => ({ ...prev, plugin }))
              }}
            />
          </div>

          {/* Tool Catalog */}
          <div className="lg:col-span-9 h-full overflow-y-auto rounded-xl bg-surface border border-surface-border p-4">
            <ToolCatalog
              tools={filteredByPlugin}
              filters={filters}
              viewMode={viewMode}
              loading={loading}
              onTryTool={setTestingTool}
            />
          </div>
        </div>
      </div>

      {/* Tool Testing Panel */}
      {testingTool && (
        <ToolTestingPanel
          tool={testingTool}
          onClose={() => setTestingTool(null)}
          onExecute={handleExecuteTool}
        />
      )}
    </div>
  )
}
